import { Helmet } from 'react-helmet'
import { Box, Dialog, Grid } from '@mui/material'
import { IconEye, IconStar, IconUsers } from '@tabler/icons-react'
import React, { useCallback, useEffect, useState } from 'react'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../store'
import {
  selectRestaurantAlias,
  selectRestaurantLocation,
} from '../../../store/authSlice'
import {
  fetchCompetitorCuisine,
  fetchCompetitorDietary,
  fetchCompetitorPopularDishes,
  fetchCompetitorPricing,
  fetchRatingSummary,
  fetchRecommendedSummary,
  fetchRestaurantDashboard,
  fetchSentimentSummary,
  fetchSuggestedIngredients,
  fetchViewsSummary,
  fetchVisitors,
  selectDashboardDetail,
  selectEndDate,
  selectMessage,
  selectRatingSummary,
  selectRatingSummaryLoading,
  selectRecommendedSummary,
  selectRecommendedSummaryLoading,
  selectRestaurantFIPS,
  selectRestaurantMiles,
  selectSentimentSummary,
  selectSentimentSummaryLoading,
  selectSeverity,
  selectShouldOpen,
  selectStartDate,
  selectViewsSummary,
  selectViewsSummaryLoading,
  setOpen,
  setRestaurantFIPS,
} from '../../../store/restaurant/dashboard'
import {
  fetchRestaurantAdmin,
  selectRestaurant,
} from '../../../store/restaurant/setting'
import { IDashboardCard } from '../../../types/dashboard'
import { MuiAlert, MuiSnackbar } from '../../eater/styled/global.styled'
import CompetitorInsights from './components/CompetitorInsights'
import DashboardHeader from './components/DashboardHeader'
import PopularDishesTable from './components/PopularDishesTable'
import TopCard from './components/TopCard'
import VisitorsModal from './components/VisitorsModal'
import Rating from './components/rating/Rating'
import { loadGoogleMaps } from '../../../helpers/googleMapsLoader'
import { DashboardMainGrid } from './styled/Dashboard.styled'
import {
  findFipsByCountyState,
  getCountyAndStateByPlaceId,
} from '../../../helpers/fips-code-helper'

const Dashboard: React.FC = () => {
  const { theme, dispatch } = useAppUtilities()
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY
  const openServer = useAppSelector(selectShouldOpen)
  const message = useAppSelector(selectMessage)
  const severity = useAppSelector(selectSeverity)
  const restaurantLocation = useAppSelector(selectRestaurantLocation)
  const restaurantAlias = useAppSelector(selectRestaurantAlias)
  const restaurant = useAppSelector(selectRestaurant)
  // eslint-disable-next-line
  const dashboardDetail = useAppSelector(selectDashboardDetail)
  const restaurantMiles = useAppSelector(selectRestaurantMiles)
  const fromDate = useAppSelector(selectStartDate)
  const adminFIPS = useAppSelector(selectRestaurantFIPS)
  const toDate = useAppSelector(selectEndDate)
  const [cardModal, setCardModal] = useState<boolean>(false)
  const [isMapLoad, setIsMapLoad] = useState<boolean>(false)
  const recommendedSummary = useAppSelector(selectRecommendedSummary)
  const viewsSummary = useAppSelector(selectViewsSummary)
  const sentimentSummary = useAppSelector(selectSentimentSummary)
  const ratingSummary = useAppSelector(selectRatingSummary)
  const recommendedSummaryLoading = useAppSelector(selectRecommendedSummaryLoading)
  const viewsSummaryLoading = useAppSelector(selectViewsSummaryLoading)
  const sentimentSummaryLoading = useAppSelector(selectSentimentSummaryLoading)
  const ratingSummaryLoading = useAppSelector(selectRatingSummaryLoading)

  useEffect(() => {
    void loadSummaryData()
  }, [fromDate, toDate])

  // Fetch the alias-keyed restaurant admin profile only once the owner's alias is in state — fixes
  // the mount-race that fired /restaurants//admin before the alias loaded.
  useEffect(() => {
    if (restaurantAlias) void getRestaurant()
  }, [restaurantAlias])

  useEffect(() => {
    void onCompetitorPricing()
    if (googleMapsApiKey) {
      // Shared single-load: dedupes against CompetitorInsightsMap so the Maps JS API is injected
      // once, not once per component.
      void loadGoogleMaps(googleMapsApiKey)
        .then(() => setIsMapLoad(true))
        .catch(() => {
          // Maps unavailable — the map-dependent widgets simply won't render.
        })
    }
  }, [])

  useEffect(() => {
    void onCompetitorPopularDishes()
  }, [fromDate, toDate, restaurantMiles])

  useEffect(() => {
    void onCompetitorPricing()
    void onCompetitorCuisine()
    void onCompetitorDietary()
    void onCompetitorSuggestedIngredients()
  }, [restaurantMiles])

  useEffect(() => {
    void findFipsCode()
  }, [isMapLoad, window.googleMapsLoaded, restaurant?.placeId])

  const onCompetitorPricing = async (): Promise<void> => {
    await dispatch(
      fetchCompetitorPricing({
        longitude: restaurantLocation?.lng ?? -73.935242,
        latitude: restaurantLocation?.lat ?? 40.73061,
        radius: restaurantMiles ?? 5,
      }),
    )
  }
  const onCompetitorPopularDishes = async (): Promise<void> => {
    await dispatch(
      fetchCompetitorPopularDishes({
        longitude: restaurantLocation?.lng ?? -73.935242,
        latitude: restaurantLocation?.lat ?? 40.73061,
        radius: restaurantMiles ?? 5,
        fromDate,
        toDate,
      }),
    )
  }
  const onCompetitorDietary = async (): Promise<void> => {
    await dispatch(
      fetchCompetitorDietary({
        longitude: restaurantLocation?.lng ?? -73.935242,
        latitude: restaurantLocation?.lat ?? 40.73061,
        radius: restaurantMiles ?? 5,
      }),
    )
  }
  const onCompetitorCuisine = async (): Promise<void> => {
    await dispatch(
      fetchCompetitorCuisine({
        longitude: restaurantLocation?.lng ?? -73.935242,
        latitude: restaurantLocation?.lat ?? 40.73061,
        radius: restaurantMiles ?? 5,
      }),
    )
  }
  const onCompetitorSuggestedIngredients = async (): Promise<void> => {
    await dispatch(
      fetchSuggestedIngredients({
        longitude: restaurantLocation?.lng ?? -73.935242,
        latitude: restaurantLocation?.lat ?? 40.73061,
        radius: restaurantMiles ?? 5,
        fromDate,
        toDate,
      }),
    )
  }
  const onVisitors = async (): Promise<void> => {
    await dispatch(
      fetchVisitors({
        fromDate,
        toDate,
      }),
    )
  }

  const getRestaurant = async (): Promise<void> => {
    // Don't fire with an empty alias — that builds /restaurants//admin (malformed, CORS-blocked).
    if (!restaurantAlias) return
    await dispatch(fetchRestaurantAdmin({ alias: restaurantAlias }))
  }

  const findFipsCode = async (): Promise<void> => {
    if (!adminFIPS && (window.googleMapsLoaded === true || isMapLoad)) {
      if (!restaurant?.placeId) {
        dispatch(setRestaurantFIPS(null))
        return
      }

      const countyName = await getCountyAndStateByPlaceId(restaurant.placeId)

      if (countyName) {
        const fipsCode = await findFipsByCountyState(
          countyName.county,
          countyName.countyState,
        )
        dispatch(setRestaurantFIPS(fipsCode))
      } else {
        dispatch(setRestaurantFIPS(null))
      }
    }
  }

  const loadSummaryData = async (): Promise<void> => {
    // Load all summary data independently
    void dispatch(
      fetchRecommendedSummary({ fromDate, toDate }),
    )
    void dispatch(fetchViewsSummary({ fromDate, toDate }))
    void dispatch(fetchSentimentSummary({ fromDate, toDate }))
    void dispatch(fetchRatingSummary({ fromDate, toDate }))
    
    // Load other dashboard data. The restaurant admin profile is alias-keyed (not date-keyed) and
    // is fetched by its own effect once the alias resolves — see the restaurantAlias effect below.
    await Promise.all([
      dispatch(fetchRestaurantDashboard({ fromDate, toDate })),
      onVisitors(),
    ])
  }

  const onTopCardDetail = useCallback((): IDashboardCard[] => {
    return [
      {
        title: 'Total Views',
        value: viewsSummary?.value ?? 0,
        percentageChange: viewsSummary?.change ?? 0,
        icon: (
          <IconUsers
            stroke={1.5}
            style={{ color: theme.palette.secondary.main, fontSize: 28 }}
          />
        ),
        isInfo: false,
        isModelShow: true,
        isLoading: viewsSummaryLoading,
      },
      {
        title: 'Average Rating',
        value: Number(ratingSummary?.value?.toFixed(1)) ?? 0,
        percentageChange: ratingSummary?.change ?? 0,
        icon: (
          <IconStar
            stroke={1.5}
            style={{ color: theme.palette.secondary.main, fontSize: 28 }}
          />
        ),
        isInfo: false,
        isLoading: ratingSummaryLoading,
      },
      {
        title: 'Recommended to Users',
        value: recommendedSummary?.value ?? 0,
        percentageChange: recommendedSummary?.change ?? 0,
        icon: (
          <IconEye
            stroke={1.5}
            style={{ color: theme.palette.secondary.main, fontSize: 28 }}
          />
        ),
        isInfo: true,
        isLoading: recommendedSummaryLoading,
      },
      {
        title: 'Reviews',
        value: sentimentSummary?.value ?? 0,
        percentageChange: sentimentSummary?.change ?? 0,
        icon: (
          <IconStar
            stroke={1.5}
            style={{ color: theme.palette.secondary.main, fontSize: 28 }}
          />
        ),
        isInfo: false,
        isLoading: sentimentSummaryLoading,
      },
    ]
  }, [
    viewsSummary,
    ratingSummary,
    recommendedSummary,
    sentimentSummary,
    viewsSummaryLoading,
    ratingSummaryLoading,
    recommendedSummaryLoading,
    sentimentSummaryLoading,
    theme,
  ])

  const setOpenServer = (): void => {
    dispatch(setOpen(false))
  }

  return (
    <DashboardHeader
      title="Dashboard"
      body={
        <>
          <Helmet><title>Resactly | Restaurant Dashboard</title></Helmet>
          <Box>
            <DashboardMainGrid>
              {onTopCardDetail().map((item, index) => (
                <Grid item key={index} xs={6} lg={3}>
                  <TopCard
                    card={item}
                    onCardClick={() => setCardModal(true)}
                  />
                </Grid>
              ))}
            </DashboardMainGrid>
                <DashboardMainGrid py={{ xs: '6px', sm: 1, md: 2 }}>
                  <Grid item xs={12} md={6}>
                    <Rating />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PopularDishesTable />
                  </Grid>
                </DashboardMainGrid>
            <CompetitorInsights />
          </Box>

          <Dialog
            onClose={setCardModal}
            open={Boolean(cardModal)}
            PaperProps={{
              sx: {
                bgcolor: 'background.paper',
                borderRadius: '16px',
                p: { xs: '16px', md: '24px' },
                width: { xs: 'calc(100% - 20px)', md: '500px' },
              },
            }}
          >
            <VisitorsModal handleClose={() => setCardModal(false)} />
          </Dialog>

          <MuiSnackbar open={openServer} onClose={setOpenServer}>
            <MuiAlert onClose={setOpenServer} severity={severity ?? 'success'}>
              {message}
            </MuiAlert>
          </MuiSnackbar>
        </>
      }
    />
  )
}

export default Dashboard
